import type { ChangeEvent, ReactNode } from 'react';

export function Panel({
  label,
  meta,
  children,
  tone = 'default',
}: {
  label: string;
  meta?: ReactNode;
  children: ReactNode;
  tone?: 'default' | 'accent';
}) {
  return (
    <section className={`panel${tone === 'accent' ? ' panel--accent' : ''}`}>
      <header className="panel__head">
        <h2 className="panel__label">{label}</h2>
        {meta ? <div className="panel__meta">{meta}</div> : null}
      </header>
      <div className="panel__body">{children}</div>
    </section>
  );
}

export function Row({
  label,
  hint,
  children,
  align = 'center',
}: {
  label: string;
  hint?: string;
  children?: ReactNode;
  align?: 'center' | 'start';
}) {
  return (
    <div className={`row row--${align}`}>
      <div className="row__text">
        <span className="row__label">{label}</span>
        {hint ? <span className="row__hint">{hint}</span> : null}
      </div>
      {children ? <div className="row__control">{children}</div> : null}
    </div>
  );
}

export interface Option<T> {
  value: T;
  label: string;
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  size = 'md',
  name,
}: {
  options: ReadonlyArray<Option<T>>;
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  name: string;
}) {
  return (
    <div className={`segmented segmented--${size}`} role="radiogroup" aria-label={name}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={`segmented__item${option.value === value ? ' is-active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`switch${checked ? ' is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="switch__knob" />
    </button>
  );
}

export function Slider({
  value,
  min,
  max,
  step,
  onChange,
  label,
  format,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  label: string;
  format?: (value: number) => string;
}) {
  const handleInput = (event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value));
  return (
    <div className="slider">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={handleInput}
        style={{ ['--fill' as string]: `${((value - min) / (max - min)) * 100}%` }}
      />
      {format ? <span className="slider__value">{format(value)}</span> : null}
    </div>
  );
}

export function Select({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: ReadonlyArray<Option<string>>;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <div className="select">
      <select
        value={value}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="select__caret" aria-hidden="true" />
    </div>
  );
}

export function Corners() {
  return (
    <div className="corners" aria-hidden="true">
      <span className="corner corner--tl" />
      <span className="corner corner--tr" />
      <span className="corner corner--bl" />
      <span className="corner corner--br" />
    </div>
  );
}

export function StatusPill({
  tone,
  children,
}: {
  tone: 'idle' | 'live' | 'busy' | 'alert';
  children: ReactNode;
}) {
  return (
    <span className={`pill pill--${tone}`}>
      <span className="pill__dot" aria-hidden="true" />
      {children}
    </span>
  );
}
